/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import { ISettingGroupDefinition } from "@nuance/hierarchical-settings-ui-domain";
import { CmdBuilder, IViewModel } from "@nuance/hap-components";

/**
 * Enumerations of the various mainframe views
 */
export enum Views {
    /**
     * Start view
     */
    Start,
    /**
     * Main view
     */
    Main,
    /**
     * Environmental view
     */
    EnvironmentSettings,
    /**
     * Rules View
     */
    Advanced
}

/**
 * MainFrame state
 */
export interface IMainFrameState {
    /**
     * Currently visible view
     */
    currentView: Views;
    definition?: ISettingGroupDefinition;
}

/**
 * MainFrame commands and command handlers
 */
export class MainFrameCommands {
    constructor() {
        const cb = new CmdBuilder();
        const cmds = {
            openStartView: cb.bindCmdNP(this.openStartViewHandler),
            openMainView: cb.bindCmd(this.openMainViewHandler),
            openAdvancedView: cb.bindCmd(this.openAdvancedViewHandler)
        };

        this.Cmds = cmds;
        this.Reducer = cb.buildReducer(cmds);
    }

    readonly Cmds;
    readonly Reducer;

    /**
     * Opens the StartView
     * @param state Current MainFrame state
     */
    openStartViewHandler = (state: IMainFrameState) => {
        state.currentView = Views.Start;
        state.definition = undefined;

        return state;
    };

    /**
     * Opens the MainView
     * @param state Old Mainframe state
     */
    openMainViewHandler = (state: IMainFrameState, definition: ISettingGroupDefinition) => {
        state.currentView = Views.Main;
        state.definition = definition;
        return state;
    };

    /**
     * Opens the RulesView
     * @param state Mainframe state
     */
    openAdvancedViewHandler = (state: IMainFrameState, definition: ISettingGroupDefinition) => {
        state.currentView = Views.Advanced;
        state.definition = definition;
        return state;
    };
}

/**
 * Type for commands in the MainViewModel
 */
type IMainFrameCmds = MainFrameCommands["Cmds"];

/**
 * MainViewModel interface
 */
export type IMainFrameViewModel = IViewModel<IMainFrameState, IMainFrameCmds>;
